import { useCallback, useContext, useEffect, useState } from 'react';

import * as EmpresaAPI from '../../../api/empresa';
import * as UnidadeCondominialAPI from '../../../api/unidadeCondominial';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import styles from '../../form/Form.module.css';

import Form from './Form';

function criarEstadoInicial(empresaId) {
    return {
        empresaId: Number(empresaId) || '',
        codigo: '',
        descricao: '',
        quadra: '',
        lote: '',
        area: '',
        diaVencimento: '',
        ativo: true,
        observacao: '',
    };
}

function CreateOrEdit() {
    const { parametro } = useContext(Context);
    const { empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const pathname = '/UnidadesCondominiais';
    const urlParams = new URLSearchParams(window.location.search);

    const uuid = urlParams.get('uuid');
    const editando = Boolean(uuid && uuid !== 'null');

    const [token] = useState(localStorage.getItem('token') || '');

    const [dados, setDados] = useState(() => criarEstadoInicial(empresaId));

    const [optionsEmpresa, setOptionsEmpresa] = useState([]);
    const [carregando, setCarregando] = useState(editando);
    const [salvando, setSalvando] = useState(false);
    const [falhaCarregamento, setFalhaCarregamento] = useState(false);

    const carregarUnidade = useCallback(async () => {
        if (!editando || !uuid) {
            return null;
        }

        setCarregando(true);
        setFalhaCarregamento(false);

        try {
            const response = await UnidadeCondominialAPI.consultar(uuid);

            const unidade = response.data?.data || response.data;

            setDados((anterior) => ({
                ...anterior,
                ...unidade,
                empresaId: Number(unidade.empresaId) || '',
                area: unidade.area === null || unidade.area === undefined ? '' : unidade.area,
            }));

            return unidade;
        } catch (error) {
            setFalhaCarregamento(true);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível carregar a unidade condominial.',
                error.response?.data?.type || 'error'
            );

            return null;
        } finally {
            setCarregando(false);
        }
    }, [editando, setFlashMessage, uuid]);

    const consultarEmpresas = useCallback(async () => {
        const result = await EmpresaAPI.empresa();

        setOptionsEmpresa(result);

        return result;
    }, []);

    useEffect(() => {
        consultarEmpresas();
    }, [consultarEmpresas]);

    useEffect(() => {
        if (!token) {
            navigate('/Auth', {
                replace: true,
            });
        }
    }, [navigate, token]);

    useEffect(() => {
        carregarUnidade();
    }, [carregarUnidade]);

    async function salvarUnidade(novosDados) {
        if (salvando) {
            return;
        }

        const areaTexto = String(novosDados.area ?? '').trim();

        const area = areaTexto === '' ? null : Number(areaTexto.replace(',', '.'));

        if (area !== null && !Number.isFinite(area)) {
            setFlashMessage('Informe uma área válida.', 'warning');

            return;
        }

        const diaTexto = String(novosDados.diaVencimento ?? '').trim();
        const diaVencimento = diaTexto === '' ? null : Number(diaTexto);
        if (diaVencimento !== null && (!/^\d+$/.test(diaTexto) || diaVencimento < 1 || diaVencimento > 31)) {
            setFlashMessage(
                'Informe um dia de vencimento inteiro entre 1 e 31, ou deixe vazio para usar o padrão da empresa.',
                'warning'
            );
            return;
        }
        const payload = {
            diaVencimento,
            empresaId: Number(novosDados.empresaId),

            codigo: String(novosDados.codigo || '')
                .trim()
                .toUpperCase(),

            descricao: String(novosDados.descricao || '').trim(),

            portaria: Boolean(novosDados.portaria),

            quadra: String(novosDados.quadra || '')
                .trim()
                .toUpperCase(),

            lote: String(novosDados.lote || '')
                .trim()
                .toUpperCase(),

            area,

            ativo: Boolean(novosDados.ativo),

            observacao: String(novosDados.observacao || '').trim() || null,
        };

        setSalvando(true);

        try {
            let urlNavigate = pathname;
            const response = editando
                ? await UnidadeCondominialAPI.editar(uuid, payload)
                : await UnidadeCondominialAPI.cadastrar(payload);

            setFlashMessage(response.data.message, response.data.type);

            const sucesso = response.status >= 200 && response.status < 300 && response.data?.type === 'success';

            if (!sucesso) {
                return;
            }

            const params = new URLSearchParams(urlParams);

            params.delete('uuid');
            params.delete('btnText');

            navigate(pathname, params);
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || error.message || 'Não foi possível salvar a unidade condominial.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setSalvando(false);
        }
    }

    return (
        <section className={`${styles.form_container} ${styles[parametro.tema]}`}>
            <Form
                dados={dados}
                setDados={setDados}
                handleSubmit={salvarUnidade}
                caminhoVoltar={pathname}
                OptionsEmpresa={optionsEmpresa}
                editando={editando}
                carregando={carregando}
                salvando={salvando}
                falhaCarregamento={falhaCarregamento}
            />
        </section>
    );
}

export default CreateOrEdit;
